import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { QueryClientProvider } from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import { clearCurrentUser, ensureCurrentUser } from "~/services/CurrentUser";
import { globalEventEmitter, GlobalEvents } from "~/services/GlobalEventEmitter";
import { queryClient } from "~/services/queryClient";
import { TempPasswordProvider } from "./routes/forgot-password/context/TempPasswordContext";
import { isPublicPath } from "./utils/publicRoutes";
import { themeSwitchContext, ThemeSwitchProvider } from "./utils/ThemeSwitchContext";

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" style={{ height: "100%" }}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <title>Educator Passport</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
        <Meta />
        <Links />
      </head>
      <body style={{ height: "100%" }}>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function AppContent() {
  const themeSwitch = useContext(themeSwitchContext);
  const location = useLocation();
  const navigate = useNavigate();

  const injectTheme = async () => {
    if (isPublicPath(location.pathname)) {
      return;
    }

    // Shares the cache with useCurrentUser, so this is no longer a duplicate request.
    const user = await ensureCurrentUser().catch(() => undefined);
    if (themeSwitch.updateTheme && user) {
      themeSwitch.updateTheme(
        `#${user.institute.primary_color}`,
        `#${user.institute.secondary_color}`,
        `#${user.institute.primary_light_color}`,
      );
    }
  };

  useEffect(() => {
    injectTheme();
  }, [location]);

  // An expired or revoked session shows up as a 401 on whatever call happens to fire
  // next. Drop the cached user and send them back to login rather than leaving a
  // half-rendered page behind -- unless they are already on a signed-out route, where a
  // 401 is the expected answer and not a session that has gone.
  useEffect(() => {
    const onUnauthorized = () => {
      clearCurrentUser();

      if (!isPublicPath(window.location.pathname)) {
        navigate("/login");
      }
    };

    globalEventEmitter.on(GlobalEvents.API_REQUEST_UNAUTHORIZED, onUnauthorized);

    return () => {
      globalEventEmitter.off(GlobalEvents.API_REQUEST_UNAUTHORIZED, onUnauthorized);
    };
  }, [navigate]);

  return (
    <ThemeProvider theme={themeSwitch.theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <TempPasswordProvider>
            <Outlet />
          </TempPasswordProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong.</div>;
};

export default withSentry(function App() {
  return (
    <ThemeSwitchProvider>
      <AppContent />
    </ThemeSwitchProvider>
  );
});

export function HydrateFallback() {
  return null;
}
